import {Component, HostBinding, Inject, Injector, OnInit} from '@angular/core';
import {DIALOG_DATA, DialogRef} from '@angular/cdk/dialog';
import {BaseComponent} from '../../../base.component';
import {animationSlideInOut} from '../../../../util/animation/animation.slideInOut';
import {StringUtil} from '../../../../util/string.util';
import {SnackBarUtil} from '../../../../util/snack.bar.util';
import {DataResponse} from '../../../../network/service/data.response';
import {isEqual} from 'lodash';
import {RequestType} from '../../../../enums/request.type';
import {strings} from '../../../../../value/strings';
import {SessionStorageUtil} from '../../../../util/session.storage.util';
import {Config} from '../../../../common/config';
import {SelectAdditivesComponent} from '../select-additives/select-additives.component';

@Component({
  selector: 'app-service-details',
  standalone: false,
  templateUrl: './service-details.component.html',
  styleUrl: './service-details.component.scss',
  animations: [animationSlideInOut]
})
export class ServiceDetailsComponent extends BaseComponent implements OnInit {
  selectedAttributes: any[] = [];
  @HostBinding('@slideInOut') animationState = 'open';
  additives: any = [];
  servicePriceAdditiveId?: any;
  orders?: any = [];
  comments?: any = [];
  protected readonly StringUtil = StringUtil;
  protected readonly Config = Config;

  constructor(injector: Injector, public dialogRef: DialogRef<string>, @Inject(DIALOG_DATA) public data: any) {
    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.getOrders();
  }

  override loadOnline() {
    super.loadOnline();
    this.serviceApi.getComments(undefined, undefined, 'User,Factor,Factor.FactorOrders,Factor.FactorOrders.Order,Factor.FactorOrders.Order.ServicePrice,Factor.FactorOrders.Order.ServicePrice.Service', RequestType.ALL, Config.branch.BusinessId, {
      Filter: {
        Values: [{
          Field: 'ServiceId',
          Type: 'EQ',
          Value: this.data.Service.ServiceId,
        }]
      }
    }).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        this.comments = res.data;
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  noAction() {
    this.dialogRef.close();
  }

  onAttributeSelected(attribute: any, additive: any) {
    if (!this.selectedAttributes) {
      this.selectedAttributes = [];
    }
    let selectedForThisAdditive = this.selectedAttributes.filter(id =>
      additive.Attributes.some((attr: any) => attr.ServicePriceAdditive.ServicePriceAdditiveId === id)
    );
    if (selectedForThisAdditive.includes(attribute.ServicePriceAdditiveId)) {
      this.selectedAttributes = this.selectedAttributes.filter(id => id !== attribute.ServicePriceAdditiveId);
    } else {
      if (selectedForThisAdditive.length >= additive.MaxChoice) {
        this.selectedAttributes = this.selectedAttributes.filter(id => id !== selectedForThisAdditive[0]);
      }
      this.selectedAttributes.push(attribute.ServicePriceAdditiveId);
    }
  }

  getOrders() {
    if (this.hasAccount()) {
      this.serviceApi.getMyOrders(undefined, undefined, 'ServicePrice,ServicePrice.Service,ServicePrice.Service.ServicePrices,ServicePrice.ServicePriceAdditives,ServicePrice.ServicePriceAdditives.Additive,ServicePrice.ServicePriceAdditives.Attribute', RequestType.ALL, Config.branch.BusinessId, {
        Filter: {
          Values: [{
            Field: 'Type',
            Type: 'EQ',
            Value: strings.basket,
          }]
        }
      }).subscribe((res: DataResponse<any>) => {
        if (res.success) {
          this.orders = res.data;
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        }
      });
    } else {
      let basket: any[] = SessionStorageUtil.loadObject(strings.basket_service_ids, []);
      this.orders = basket;
      Config.basketCount = basket.length;
    }
  }

  getProductAttributePriceCount(servicePriceId: string) {
    let order = this.orders?.filter((order: any) => order.ServicePriceId === servicePriceId);
    let totalCount = 0;
    if (order) {
      for (const element of order) {
        totalCount += (element ? parseInt(element.Count) : 0);
      }
    }
    return totalCount;
  }

  onProductOrderClicked(value: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (this.data.Service.ServicePrices[0]?.ServicePriceAdditives?.length > 0) {
      if (value < 0) {
        let order = this.orders?.findLast((order: any) => {
          return order.ServicePriceId === this.data.Service.ServicePrices[0].ServicePriceId;
        });
        if (order) {
          let temp = {
            ...order.ServicePrice?.Service
          };
          temp.ServicePrices = [order.ServicePrice];
          temp.ServicePriceAdditiveIds = order.ServicePriceAdditiveIds;
          this.onAddToBasketClicked(temp, ((order?.Count ?? 0) + value));
        }
      } else {
        const dialogRef = this.matDialog.open(SelectAdditivesComponent, {
          width: '100%',
          maxWidth: '500px',
          panelClass: 'select-additives-dialog',
          hasBackdrop: false,
          data: {
            Service: this.data.Service,
            onSubmit: (service: any, additives: any[]) => {
              service.ServicePriceAdditiveIds = additives;
              this.data.onSubmit(service, additives);
              dialogRef.close();
            }
          }
        });
        dialogRef.afterClosed().subscribe(() => {
        });
      }
    } else {
      let order = this.orders?.find((order: any) => {
        return order.ServicePriceId === this.data.Service.ServicePrices[0].ServicePriceId;
      });
      if (order) {
        let temp = {
          ...order.ServicePrice?.Service
        };
        temp.ServicePrices = [order.ServicePrice];
        temp.ServicePriceAdditiveIds = order.ServicePriceAdditiveIds;
        this.onAddToBasketClicked(temp, ((order?.Count ?? 0) + value));
      } else {
        this.onAddToBasketClicked(this.data.Service, value);
      }
    }
  }

  onAddToBasketClicked(service: any, count?: any) {
    this.onServiceClicked({
      Service: service,
      Count: count
    });
  }

  onServiceClicked(object: any) {
    if (object.Service.ServicePrices?.length > 0 && object.Service.ServicePrices[0].ServicePriceAdditives?.length > 0) {
      this.saveOrder(object, object.Service, object.Service.ServicePriceAdditiveIds);
    } else {
      this.saveOrder(object, object.Service);
    }
  }

  saveOrder(object: any, service: any, additives?: any[]) {
    if (this.hasAccount()) {
      this.serviceApi.postOrders({
        ServicePriceId: service.ServicePrices[0]?.ServicePriceId,
        Count: object.Count,
        Type: strings.basket,
        ServicePriceAdditiveIds: additives,
        BranchId: Config.branch.BusinessId
      }).subscribe((res: DataResponse<any>) => {
        if (res.success) {
          if (res.data.dataValues && !res.data.previousDataValues) {
            Config.basketCount++;
          } else if (!res.data.dataValues) {
            Config.basketCount--;
          }
          this.getOrders();
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        }
      });
    } else {
      let basket: any[] = SessionStorageUtil.loadObject(strings.basket_service_ids, []);
      let basketServicePrice: any;
      if (additives) {
        basketServicePrice = basket.find((basketServicePrice: any) => {
          return service.ServicePrices[0].ServicePriceId === basketServicePrice.ServicePriceId &&
            isEqual(basketServicePrice.ServicePriceAdditiveIds, additives);
        });
      } else {
        basketServicePrice = basket.find((basketServicePrice: any) => {
          return service.ServicePrices[0].ServicePriceId === basketServicePrice.ServicePriceId;
        });
      }
      if (basketServicePrice) {
        basketServicePrice.Count = object.Count;
      } else {
        let temp = {
          ...service.ServicePrices[0]
        };
        temp.ServicePrice = service.ServicePrices[0];
        temp.ServicePrice.Service = {...object.Service};
        delete temp.ServicePrice.Service.ServicePrices;
        temp.Count = object.Count;
        temp.ServicePriceAdditiveIds = service.ServicePriceAdditiveIds;
        basket.push(temp);
      }
      basket = basket.filter((basketServicePrice: any) => {
        return basketServicePrice.Count !== 0;
      });
      SessionStorageUtil.saveObject(strings.basket_service_ids, basket);
      this.getOrders();
      Config.basketCount = basket.length;
    }
  }
}
